// ScanJobWatcher — follows accepted scan uploads (and „Ponów przetwarzanie” batches)
// to their pipeline result.
//
// An accepted scan leaves its „Zadania w tle” item "processing" with the
// `document_jobs` id (scanJobTracking.markScanAccepted; a re-run job via
// useRerunTracking). This renderless component
// reads those jobs with their page rows embedded (realtime on both tables, plus a
// 5 s poll while anything waits), turns each into ✓ / ⚠ / ✗ via scanJobOutcome, completes the card once
// every file is resolved, and offers „Ponów” on a failed job from which nothing was
// filed. Mounted once in App.tsx inside RealtimeProvider — it needs Refine, the
// realtime registry and ProcessProvider, all ancestors there.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useList } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { useProcesses, type BatchItemStatus, type ItemCallbacks, type Process } from "@ds/actions/useProcesses";
import { UPLOAD_PROCESS_TYPE } from "@ds/actions/useUploadProcess";
import { useRealtimeChannels } from "@ds/actions/useRealtimeChannels";
import { useNotify } from "@ds/actions/useNotify";
import { errorText } from "@shared/errorText";
import { pipelineEdgeFn } from "@services";
import { useGate } from "../../hooks/useRoleGuard";
import {
  SCAN_JOB_SELECT,
  deriveScanJobOutcome, describeScanJobOutcome,
  type ScanJobWithPages,
} from "./scanJobOutcome";
import { SCAN_RERUN_PROCESS_TYPE } from "./scanJobTracking";

// Fallback poll while anything waits: realtime is joined only then, so an event can
// land before the channel is up (and a stuck job sends none). One small query per tick.
const POLL_MS = 5_000;
const REALTIME_TABLES = ["document_jobs", "document_extractions"];

interface TrackedItem {
  processId: string;
  itemId: string;
  jobId: string;
  status: BatchItemStatus;
  message?: string;
  /** When the card started — the item has been waiting at most since then. */
  since: number;
  /** Rows read before this describe an earlier run of the job — for a re-run item, the
   *  moment its re-run call returned (`waitingSince`), not the card's start: the calls
   *  run one by one, and a read in between still shows the job's old run. */
  freshAfter: number;
}

// Accepted scan uploads, and „Ponów przetwarzanie” batches from /dokumenty.
const FOLLOWED_TYPES = new Set([UPLOAD_PROCESS_TYPE, SCAN_RERUN_PROCESS_TYPE]);

function collectTracked(processes: Process[]): TrackedItem[] {
  const out: TrackedItem[] = [];
  for (const p of processes) {
    if (!FOLLOWED_TYPES.has(p.type)) continue;
    for (const i of p.items) {
      if (!i.jobId) continue;
      const since = p.createdAt.getTime();
      out.push({
        processId: p.id, itemId: i.id, jobId: i.jobId, status: i.status, message: i.message, since,
        freshAfter: p.type === SCAN_RERUN_PROCESS_TYPE ? (i.waitingSince ?? since) : 0,
      });
    }
  }
  return out;
}

const uniqueSorted = (ids: string[]) => [...new Set(ids)].sort();

export const ScanJobWatcher: React.FC = () => {
  const { t } = useTranslation();
  const { processes, updateItem, awaitJob, settleProcess, reopenProcess, registerCallbacks } = useProcesses();
  const canRerun = useGate("pipeline_rerun");
  const notify = useNotify();
  const realtime = useRealtimeChannels();
  const [now, setNow] = useState(() => Date.now());
  // jobId → when this viewer re-ran it. Rows fetched before that describe the old run.
  const retriedAt = useRef(new Map<string, number>());
  // jobIds whose re-run call is in flight.
  const retrying = useRef(new Set<string>());
  // processId → the item ids whose „Ponów” is currently registered.
  const registered = useRef(new Map<string, string>());
  const refetchRef = useRef<() => void>(() => {});

  const tracked = useMemo(() => collectTracked(processes), [processes]);
  const watchedIds = useMemo(
    () => uniqueSorted(tracked.filter((i) => i.status === "processing").map((i) => i.jobId)),
    [tracked],
  );
  // Failed items are read too (only for viewers who may re-run) to decide on „Ponów”.
  const queryIds = useMemo(
    () => uniqueSorted([...watchedIds, ...(canRerun ? tracked.filter((i) => i.status === "error").map((i) => i.jobId) : [])]),
    [tracked, watchedIds, canRerun],
  );
  const enabled = queryIds.length > 0;

  const { query: jobsQuery } = useList<ScanJobWithPages>({
    resource: "document_jobs",
    filters: [{ field: "id", operator: "in", value: queryIds }],
    pagination: { mode: "off" },
    meta: { select: SCAN_JOB_SELECT },
    queryOptions: { enabled },
  });

  const { refetch } = jobsQuery;
  useEffect(() => {
    refetchRef.current = () => { void refetch(); };
  }, [refetch]);

  // Live while anything waits: realtime on both tables + a poll that also advances
  // `now` for the stall check (realtime can drop events; a stuck job sends none).
  const watching = watchedIds.length > 0;
  useEffect(() => {
    if (!watching) return;
    const refetch = () => refetchRef.current();
    REALTIME_TABLES.forEach((table) => realtime?.register(table, refetch));
    const timer = setInterval(() => { setNow(Date.now()); refetch(); }, POLL_MS);
    return () => {
      clearInterval(timer);
      REALTIME_TABLES.forEach((table) => realtime?.unregister(table, refetch));
    };
  }, [watching, realtime]);

  const ready = jobsQuery.isSuccess;
  const readFailed = jobsQuery.isError;
  const jobRows = jobsQuery.data?.data;
  const fetchedAt = jobsQuery.dataUpdatedAt;

  // Resolve waiting items from the fetched rows; complete a card once all its files are resolved.
  // While the read fails there is no job state to go by: only the stall rule (measured from
  // when the item started waiting) may resolve an item, so a broken read can't pin it forever.
  useEffect(() => {
    if (!ready && !readFailed) return;
    const jobs = new Map((ready ? jobRows ?? [] : []).map((j) => [j.id, j]));
    const touched = new Set<string>();
    let stale = false;
    for (const item of tracked) {
      if (item.status !== "processing") continue;
      // A re-run (this viewer's „Ponów”, or a „Ponów przetwarzanie” card) makes rows
      // read before it stale — the same job id may already sit in the query cache.
      const retried = Math.max(retriedAt.current.get(item.jobId) ?? 0, item.freshAfter);
      if (ready && retried > fetchedAt) { stale = true; continue; }
      const job = jobs.get(item.jobId);
      const outcome = deriveScanJobOutcome({ job, pages: job?.pages ?? [], now, since: Math.max(item.since, retried) });
      if (!ready && outcome.kind !== "stalled") continue;
      const next = describeScanJobOutcome(outcome, t, { canRetry: canRerun });
      if (next.status === item.status && next.message === item.message) continue;
      updateItem(item.processId, item.itemId, next.status, next.message);
      touched.add(item.processId);
    }
    touched.forEach((pid) => settleProcess(pid));
    // An unchanged id list doesn't refetch on its own — don't wait for the poll.
    if (stale) refetchRef.current();
  }, [ready, readFailed, jobRows, fetchedAt, tracked, now, t, canRerun, updateItem, settleProcess]);

  const retry = useCallback(async (processId: string, itemId: string, jobId: string) => {
    // The item stays "error" (button clickable) until the re-run call returns; a second
    // click meanwhile would start a second re-run of the same job.
    if (retrying.current.has(jobId)) return;
    retrying.current.add(jobId);
    try {
      await pipelineEdgeFn.rerunJob(jobId);
    } catch (e) {
      const detail = errorText(e);
      notify.error(detail ? `${t("pipeline.tracking.retryFailed")}: ${detail}` : t("pipeline.tracking.retryFailed"));
      return;
    } finally {
      retrying.current.delete(jobId);
    }
    retriedAt.current.set(jobId, Date.now());
    reopenProcess(processId);
    awaitJob(processId, itemId, jobId, t("pipeline.tracking.processing"));
    refetchRef.current();
  }, [notify, t, reopenProcess, awaitJob]);

  // „Ponów” on failed items whose job filed nothing, for viewers allowed to re-run.
  // Re-registered only when the set of such items changes (registering re-renders).
  useEffect(() => {
    if (!ready) return;
    const jobs = new Map((jobRows ?? []).map((j) => [j.id, j]));
    const byProcess = new Map<string, Map<string, ItemCallbacks>>();
    for (const item of tracked) {
      if (!canRerun || item.status !== "error") continue;
      const job = jobs.get(item.jobId);
      if (!job) continue;
      const outcome = deriveScanJobOutcome({ job, pages: job.pages ?? [], now, since: item.since });
      if (outcome.kind !== "failed" || !outcome.retryable) continue;
      const cbs = byProcess.get(item.processId) ?? new Map<string, ItemCallbacks>();
      cbs.set(item.itemId, { retry: () => retry(item.processId, item.itemId, item.jobId) });
      byProcess.set(item.processId, cbs);
    }
    for (const pid of new Set(tracked.map((i) => i.processId))) {
      const cbs = byProcess.get(pid) ?? new Map<string, ItemCallbacks>();
      const key = [...cbs.keys()].sort().join(",");
      if ((registered.current.get(pid) ?? "") === key) continue;
      registered.current.set(pid, key);
      registerCallbacks(pid, cbs);
    }
  }, [ready, jobRows, tracked, now, canRerun, retry, registerCallbacks]);

  return null;
};
